import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, ChevronDown, ChevronRight, AlertTriangle, Truck, Users, Tag, IndianRupee, Loader2, Info } from "lucide-react";

const formatINR = (paise) => {
  const r = (paise || 0) / 100;
  if (Math.abs(r) >= 10000000) return `${r < 0 ? "-" : ""}₹${Math.abs(r / 10000000).toFixed(2)}Cr`;
  if (Math.abs(r) >= 100000) return `${r < 0 ? "-" : ""}₹${Math.abs(r / 100000).toFixed(2)}L`;
  if (Math.abs(r) >= 1000) return `${r < 0 ? "-" : ""}₹${Math.abs(r / 1000).toFixed(1)}K`;
  return `₹${r.toFixed(0)}`;
};

const VERDICT_STYLES = {
  GREEN:  { label: "Healthy",   color: "emerald", icon: TrendingUp,    note: "Strong margin — this dealer is genuinely profitable" },
  AMBER:  { label: "Watch",     color: "amber",   icon: TrendingUp,    note: "Acceptable margin — keep monitoring scheme & service costs" },
  ORANGE: { label: "Thin",      color: "orange",  icon: AlertTriangle, note: "Top-line looks good but margin is razor-thin once costs are deducted" },
  RED:    { label: "Loss-making", color: "red",   icon: TrendingDown,  note: "Costs to serve EXCEED revenue. Renegotiate scheme cost, raise price, or reduce visits." },
  NO_DATA:{ label: "No data",   color: "slate",   icon: AlertTriangle, note: "No orders yet — margin will appear after the first dispatch" },
};

/**
 * <DealerTrueMarginCard>
 * Lifetime true-margin view: revenue minus (scheme discounts + salesman cost + delivery).
 * Designed to slot into DealerCatalogueDialog right after the ledger snapshot.
 */
export function DealerTrueMarginCard({ partyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!partyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/dealers/${partyId}/true-margin`);
        const j = await res.json();
        if (!cancelled && j.success) setData(j.data);
      } catch { /* swallow */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [partyId]);

  if (loading) {
    return <Card className="p-3 flex items-center justify-center text-slate-400" data-testid="true-margin-loading"><Loader2 className="w-4 h-4 animate-spin" /></Card>;
  }
  if (!data) return null;

  const meta = VERDICT_STYLES[data.verdict] || VERDICT_STYLES.NO_DATA;
  const Icon = meta.icon;
  const isLoss = data.true_margin < 0;

  // Component-icon map for the breakdown rows
  const componentIcons = {
    "Revenue (lifetime)": IndianRupee,
    "Scheme discounts given": Tag,
    "Salesman time-cost": Users,
  };

  return (
    <Card
      className={`border-2 border-${meta.color}-200 bg-gradient-to-br from-${meta.color}-50/40 via-white to-${meta.color}-50/10 dark:from-${meta.color}-900/20 dark:to-${meta.color}-900/10 overflow-hidden`}
      data-testid="dealer-true-margin"
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 text-${meta.color}-600`} />
            <h3 className={`text-sm font-bold text-${meta.color}-900 dark:text-${meta.color}-200`}>True Margin Analysis</h3>
            <Badge variant="outline" className={`text-[10px] bg-${meta.color}-100 text-${meta.color}-700 border-${meta.color}-300`} data-testid="margin-verdict">
              {meta.label}
            </Badge>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold font-mono ${isLoss ? "text-red-700" : "text-emerald-700"}`} data-testid="true-margin-value">
              {formatINR(data.true_margin)}
            </p>
            <p className="text-[10px] text-slate-500">
              {data.margin_pct}% of revenue · lifetime
            </p>
          </div>
        </div>

        {/* Verdict explainer */}
        <p className={`text-xs text-${meta.color}-700 dark:text-${meta.color}-300 mt-2 italic`}>{meta.note}</p>

        {/* Compact summary strip */}
        <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
          <div className="px-2 py-1.5 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
            <p className="text-[10px] text-emerald-700 uppercase tracking-wider">Revenue</p>
            <p className="text-sm font-bold font-mono text-emerald-900 dark:text-emerald-200">{formatINR(data.revenue)}</p>
          </div>
          <div className="px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
            <p className="text-[10px] text-amber-700 uppercase tracking-wider">Schemes</p>
            <p className="text-sm font-bold font-mono text-amber-900 dark:text-amber-200">−{formatINR(data.scheme_cost)}</p>
          </div>
          <div className="px-2 py-1.5 rounded bg-violet-50 dark:bg-violet-900/20 border border-violet-200">
            <p className="text-[10px] text-violet-700 uppercase tracking-wider flex items-center gap-1">
              Salesmen
              {data.salesman_cost === 0 && data.salaried_unlinked_count > 0 && (
                <span
                  className="inline-flex items-center cursor-help"
                  title={`${data.salaried_unlinked_count} salaried employee(s) have no orders attributed to them yet — salesman cost not computed.`}
                  data-testid="salesman-zero-tooltip"
                >
                  <Info className="w-2.5 h-2.5 text-violet-500" />
                </span>
              )}
            </p>
            <p className="text-sm font-bold font-mono text-violet-900 dark:text-violet-200">−{formatINR(data.salesman_cost)}</p>
          </div>
          <div className="px-2 py-1.5 rounded bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
            <p className="text-[10px] text-slate-600 uppercase tracking-wider">Delivery</p>
            <p className="text-sm font-bold font-mono text-slate-900 dark:text-slate-200">−{formatINR(data.delivery_cost)}</p>
          </div>
        </div>

        {/* Expandable breakdown */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"
          data-testid="toggle-margin-breakdown"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {expanded ? "Hide" : "Show"} cost breakdown
        </button>

        {expanded && (
          <div className="mt-3 p-3 rounded-lg bg-slate-50/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 text-xs space-y-1.5" data-testid="margin-breakdown">
            {data.components.map((c, i) => {
              const CIcon = componentIcons[c.label] || Truck;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between gap-2 py-1">
                    <span className="flex items-center gap-1.5">
                      <CIcon className="w-3 h-3 text-slate-400 shrink-0" />
                      <span className="text-slate-700 dark:text-slate-300">{c.label}</span>
                    </span>
                    <span className={`font-mono font-semibold ${c.kind === "credit" ? "text-emerald-700" : "text-red-700"}`}>
                      {c.kind === "credit" ? "+" : "−"}{formatINR(c.value)}
                    </span>
                  </div>
                  {c.breakdown && c.breakdown.length > 0 && (
                    <div className="ml-5 mt-1 mb-1.5 space-y-0.5 text-[11px] text-slate-500" data-testid={`breakdown-${c.label.toLowerCase().replace(/\W+/g, '-')}`}>
                      {c.breakdown.map((b, j) => (
                        <div key={j} className="flex items-center justify-between gap-2">
                          <span>↳ {b.employee_name} · {b.bags_shipped} bags @ ₹{(b.cost_per_bag_paise / 100).toFixed(1)}/bag</span>
                          <span className="font-mono">−{formatINR(b.cost_paise)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="border-t pt-2 mt-1 dark:border-slate-700 flex items-center justify-between font-bold">
              <span>True margin</span>
              <span className={`font-mono ${isLoss ? "text-red-700" : "text-emerald-700"}`}>
                {formatINR(data.true_margin)} ({data.margin_pct}%)
              </span>
            </div>
            {data.ledger_outstanding > 0 && (
              <div className="text-[10px] text-amber-700 mt-1.5 flex items-center gap-1">
                <AlertTriangle className="w-2.5 h-2.5" />
                Heads-up: {formatINR(data.ledger_outstanding)} still outstanding — actual cash margin is lower.
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default DealerTrueMarginCard;
